import { MutableRefObject, useCallback, useEffect, useRef } from "react";
import axios, { AxiosInstance, CancelToken, CancelTokenSource } from "axios";
import { useDispatch } from "react-redux";
import useSetState from "../../hooks/useTypedSetState";
import { setAlert } from "../../redux/actions/actions";

const updateCancelToken = (
  cancelToken: MutableRefObject<CancelTokenSource | null | undefined>,
) => {
  if (cancelToken.current) {
    cancelToken.current.cancel("Loading cancelled");
  }
  return axios.CancelToken.source();
};

type RequestConfig<RawParams, Params, RawResponse, Response, RawData, Data> = {
  baseUrl: string;
  axiosInstance: AxiosInstance;
  method: "get" | "delete" | "post" | "put";
  buildQuery: ({
    params,
    data,
  }: {
    params?: RawParams;
    data?: RawData;
  }) => string;
  buildParams?: (params: RawParams) => Params;
  buildData?: (data: RawData) => Data;
  transformResponse?: (response: RawResponse) => Response;
  withInitialLoading?: boolean;
};

export const makeRequest = <
  RawParams,
  RawResponse,
  RawData,
  Params = never,
  Response = RawResponse,
  Data = never,
>({
  baseUrl,
  buildQuery,
  axiosInstance,
  method = "get",
  withInitialLoading = false,
  buildParams = (p: RawParams) => p as unknown as Params,
  transformResponse = (r: RawResponse) => r as unknown as Response,
  buildData,
}: RequestConfig<RawParams, Params, RawResponse, Response, RawData, Data>) => {
  const builtRequest = async (
    cancelToken: CancelToken,
    { params, requestData }: { params?: RawParams; requestData?: RawData },
  ) => {
    const url = buildQuery({ params, data: requestData });
    const preparedParams = params && buildParams(params);

    const preparedData = requestData && buildData?.(requestData);

    try {
      const { data } = await axiosInstance.request<RawResponse>({
        url,
        method,
        baseURL: baseUrl,
        params: preparedParams,
        data: preparedData,
        cancelToken,
      });

      return transformResponse(data);
    } catch (e) {
      if (axios.isCancel(e)) {
        throw e;
      }
      const err = e as any;
      console.error(
        `Unable to call ${url}, because: ${err?.response?.data?.details}`,
      );
      throw err.response.data;
    }
  };

  const emptyCancelTokenCall = () => {};

  const formatErrorMessage = (e: any): string => {
    if (typeof e === "string") return e;
    if (e?.msg) return e.msg;
    if (Array.isArray(e?.errors)) {
      return e.errors
        .flatMap((err) =>
          Object.entries(err).map(([field, message]) => `${field}: ${message}`),
        )
        .filter(Boolean)
        .join(", ");
    }
    return "An unexpected error occurred";
  };

  const useRequest = (params?: RawParams) => {
    const dispatch = useDispatch();
    const [{ data, loading, error }, setState] = useSetState({
      data: undefined as Response | undefined,
      loading: false,
      error: null as string | null,
    });

    const cancelToken = useRef<CancelTokenSource | null>();

    const request = useCallback(
      async (requestData?: RawData) => {
        const source = updateCancelToken(cancelToken);
        cancelToken.current = source;

        setState({ loading: true });

        try {
          const response = await builtRequest(source.token, {
            params,
            requestData,
          });

          setState({ error: null, data: response });

          return response;
        } catch (e) {
          const message = formatErrorMessage(e);
          if (!axios.isCancel(e)) {
            dispatch(setAlert(message, "error"));
          }
          setState({ error: message });
          throw e;
        } finally {
          setState({ loading: false });
          cancelToken.current = null;
        }
      },
      [params, setState],
    );

    useEffect(() => {
      if (withInitialLoading) {
        request();
      }
    }, [request]);

    useEffect(
      () => () => {
        cancelToken.current?.cancel();
      },
      [],
    );

    return {
      data,
      loading,
      error,
      request,
      cancelRequest: cancelToken.current?.cancel || emptyCancelTokenCall,
    };
  };

  return useRequest;
};
